"use client";

import React from "react";
import { Search, Zap, Sparkles, Globe, ArrowRight, Download } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stepsData = [{
  id: 1,
  title: "导入音频",
  description: "上传本地 MP3 文件\xA0 \xA0，AI 瞬间捕捉语音信号，开启沉浸式精听之旅。",
  icon: Download,
  hasMockup: false
}, {
  id: 2,
  title: "AI 自动处理",
  description: "智能断句、精准转写并自动生成中英对照，将复杂的语音流转化为清晰可见的文字教材。",
  icon: Zap,
  hasMockup: true
}, {
  id: 3,
  title: "精听 + 复习",
  description: "利用单句循环与 Anki 记忆算法，逐个击破听力死角，直到你能完全听懂并掌握每一句。",
  icon: Sparkles,
  hasMockup: false
}];
export default function ThreeStepsToInspireCreativity() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: stepsRef,
    isVisible: areStepsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="167951" id="只需三步蜕变听力高手" className="bg-background text-foreground relative py-24 overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#58CC02]/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#1CB0F6]/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-20 transition-all duration-1000 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Badge variant="outline" className="mb-4 border-[#58CC02]/30 text-[#58CC02] bg-[#58CC02]/5 px-4 py-1.5 rounded-full font-medium">
            高效学习法
          </Badge>
          <h2 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight text-slate-800">
            只需三步，蜕变 <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#58CC02] to-[#1CB0F6]">听力高手</span>
          </h2>
          <p className="text-xl text-slate-500 leading-relaxed">
            专为英语学习者打造的高效路径，从听不懂到开口说，只需三个简单的步骤。
          </p>
        </div>

        {}
        <div ref={stepsRef} className={`relative transition-all duration-1000 delay-200 transform ${areStepsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {}
          <div className="hidden lg:block absolute top-12 left-[16.66%] right-[16.66%] h-[3px] bg-gradient-to-r from-transparent via-[#58CC02]/20 to-transparent z-0" />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 pt-8">
            {stepsData.map((step, index) => <div key={step.id} data-index={index} className="relative group">
                {}
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 z-20">
                  <div className="size-14 rounded-full bg-white border-2 border-slate-100 flex items-center justify-center shadow-md group-hover:border-[#58CC02]/50 group-hover:shadow-[#58CC02]/20 transition-all duration-500">
                    <span className="text-2xl font-bold font-mono text-[#58CC02]">
                      {step.id}
                    </span>
                  </div>
                </div>

                <Card className="h-full pt-12 bg-white border-slate-100 rounded-[2.5rem] shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-500 relative z-10 overflow-hidden flex flex-col">
                  {}
                  <div className="absolute top-0 left-0 w-full h-[4px] bg-gradient-to-r from-transparent via-[#58CC02]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  <CardHeader className="text-center pb-4 px-8">
                    <div className="mx-auto mb-6 size-16 rounded-3xl bg-slate-50 flex items-center justify-center border border-slate-100 group-hover:bg-[#58CC02]/10 group-hover:text-[#58CC02] transition-colors duration-500">
                      <step.icon className="size-8 text-slate-400 group-hover:text-[#58CC02] transition-colors duration-500" />
                    </div>
                    <CardTitle className="text-2xl font-bold mb-3 text-slate-800">{step.title}</CardTitle>
                    <CardDescription className="text-lg leading-relaxed text-slate-500">
                      {step.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="flex-1 flex flex-col justify-end pb-10 px-8">
                    {step.hasMockup && <div className="mt-8 rounded-2xl border border-slate-100 bg-white overflow-hidden shadow-lg ring-1 ring-slate-50 relative">
                        {}
                        <div className="flex items-center px-4 py-2.5 bg-slate-50/50 border-b border-slate-100">
                          <div className="flex gap-1.5">
                            <div className="size-2.5 rounded-full bg-[#FF4B4B]/80" />
                            <div className="size-2.5 rounded-full bg-[#FFC800]/80" />
                            <div className="size-2.5 rounded-full bg-[#58CC02]/80" />
                          </div>
                          <div className="mx-auto flex h-6 w-2/3 items-center justify-center rounded-lg bg-white text-[11px] text-slate-400 font-medium truncate px-3 border border-slate-100 shadow-sm">
                            <Globe className="size-3 mr-1.5 opacity-50" />
                            app.tinglitong.com
                          </div>
                        </div>
                        {}
                        <div className="p-5 flex flex-col gap-4 bg-gradient-to-b from-white to-slate-50/30">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex-1 h-28 rounded-xl border border-slate-100 relative overflow-hidden group/img bg-slate-50">
                              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1618423/img/generic_game_ui_mockup.png" alt="AI Processing Interface" className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover/img:opacity-40 transition-opacity duration-500" />
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="bg-white/90 backdrop-blur-md px-4 py-2 rounded-xl border border-slate-100 flex items-center gap-2 text-sm font-bold shadow-md transform translate-y-0 transition-all duration-300 text-[#1CB0F6]">
                                  <Zap className="size-4 animate-pulse" />
                                  正在生成字幕...
                                </div>
                              </div>
                            </div>
                          </div>
                          {}
                          <div className="space-y-2.5 mt-1">
                            <div className="h-2.5 w-3/4 bg-[#58CC02]/20 rounded-full" />
                            <div className="h-2.5 w-1/2 bg-slate-100 rounded-full" />
                          </div>
                        </div>
                        {}
                        <div className="absolute -right-4 -bottom-4 size-16 bg-[#1CB0F6]/10 blur-2xl rounded-full animate-pulse" />
                      </div>}
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}